import * as cells from './cells';
import * as peopleCells from './people-cells';
import type { Cell, Parsed } from './cells';
import { HEADERS, REQUIRED_HEADERS, ROLE_COLUMNS } from './columns';
import type { ImportRowInput, SheetProblem } from './types';

// Lectura de la hoja "Documentos" de la plantilla (spec 2026-10-14 §2 D5): fila del Excel
// → fila del contrato del motor (POST /master-list/import). Lo que no se puede leer se
// informa con su fila del Excel y su columna, antes de enviar nada al servidor.

/** Tope de documentos por carga del servidor (POST /master-list/import). */
export const MAX_IMPORT_ROWS = 5000;

const norm = (s: string): string => s.trim().toLowerCase();

/** Índice de cada columna de la plantilla por su encabezado (sin distinguir mayúsculas). */
function columnIndex(header: Cell[]): Map<string, number> {
  const byText = new Map(header.map((h, i) => [norm(cells.text(h) ?? ''), i] as const));
  const out = new Map<string, number>();
  for (const h of HEADERS) {
    const i = byText.get(norm(h));
    if (i !== undefined) {
      out.set(h, i);
    }
  }
  return out;
}

function parseRow(
  get: (h: string) => Cell,
  report: (column: string, message: string) => void,
): ImportRowInput {
  const out: Record<string, unknown> = {};
  const take = <T>(column: string, key: string, parsed: Parsed<T>): T | undefined => {
    if ('error' in parsed) {
      report(column, parsed.error);
      return undefined;
    }
    if (parsed.ok !== undefined) {
      out[key] = parsed.ok;
    }
    return parsed.ok;
  };
  const required = (column: string, key: string, parsed: Parsed<string>, msg: string): void => {
    if (take(column, key, parsed) === undefined && !('error' in parsed)) {
      report(column, msg);
    }
  };

  const areaPath = take('Ruta de proceso', 'areaPath', cells.path(get('Ruta de proceso'), true));
  if (areaPath === undefined && cells.text(get('Ruta de proceso')) === undefined) {
    report('Ruta de proceso', 'Falta la ruta de proceso.');
  }
  const names = take(
    'Nombres de proceso',
    'areaNames',
    cells.path(get('Nombres de proceso'), false),
  );
  // Los nombres se asignan por posición: uno por cada código, o se crearía un proceso
  // con el nombre de otro nivel.
  const rawNames = cells.text(get('Nombres de proceso'));
  if (areaPath !== undefined && rawNames !== undefined && names !== undefined) {
    const levels = rawNames.split('/').map((p) => p.trim());
    if (levels.length !== areaPath.length || levels.some((p) => p === '')) {
      delete out.areaNames;
      report(
        'Nombres de proceso',
        'Escribe un nombre por cada código de la ruta, en el mismo orden y separados por "/".',
      );
    }
  }
  required(
    'Tipo de documento',
    'documentTypeCode',
    cells.limited(get('Tipo de documento'), 40, true),
    'Falta el tipo de documento.',
  );
  take('Nombre del tipo', 'documentTypeName', cells.limited(get('Nombre del tipo'), 160));
  take(
    'Frecuencia del tipo (días)',
    'reviewFrequencyDays',
    cells.positiveInt(get('Frecuencia del tipo (días)')),
  );
  required(
    'Código',
    'code',
    cells.limited(get('Código'), 60, true),
    'Falta el código del documento.',
  );
  take('Nombre', 'title', cells.limited(get('Nombre'), 300));
  take('Origen', 'origin', cells.origin(get('Origen')));
  take('Estado', 'status', cells.status(get('Estado')));
  const version = take(
    'Versión actual',
    'currentVersion',
    cells.positiveInt(get('Versión actual')),
  );
  if (version === undefined && cells.text(get('Versión actual')) === undefined) {
    report('Versión actual', 'Falta la versión actual del documento.');
  }
  take('Revisión', 'revisionLabel', cells.revision(get('Revisión')));
  take('Fecha de vigencia', 'effectiveDate', cells.date(get('Fecha de vigencia')));
  take('Fecha de aprobación', 'approvedAt', cells.date(get('Fecha de aprobación')));
  take(
    'Frecuencia propia (días)',
    'reviewFrequencyDaysOverride',
    cells.positiveInt(get('Frecuencia propia (días)')),
  );
  take('Normas', 'norms', cells.norms(get('Normas')));
  take('Dueño', 'ownerEmail', peopleCells.email(get('Dueño')));

  const responsibilities: Record<string, string>[] = [];
  for (const [column, role] of ROLE_COLUMNS) {
    const parsed = peopleCells.people(get(column));
    if ('error' in parsed) {
      report(column, parsed.error);
    } else {
      responsibilities.push(...(parsed.ok ?? []).map((p) => ({ role, ...p })));
    }
  }
  if (responsibilities.length > 50) {
    report('Elaborador', 'Una fila admite hasta 50 responsables entre todos los roles.');
  } else if (responsibilities.length > 0) {
    out.responsibilities = responsibilities;
  }
  return out as unknown as ImportRowInput;
}

export interface ParsedSheet {
  rows: ImportRowInput[];
  /** Fila del Excel de cada documento (para señalarla en la previsualización). */
  rowNumbers: number[];
  problems: SheetProblem[];
}

export function parseSheet(data: Cell[][]): ParsedSheet {
  const [header = [], ...body] = data;
  const index = columnIndex(header);
  const missing = REQUIRED_HEADERS.filter((h) => !index.has(h));
  if (missing.length > 0) {
    return {
      rows: [],
      rowNumbers: [],
      problems: [
        {
          row: 1,
          column: null,
          message: `La hoja no tiene las columnas de la plantilla (faltan: ${missing.join(', ')}). Descarga la plantilla y copia los datos en ella.`,
        },
      ],
    };
  }
  const rows: ImportRowInput[] = [];
  const rowNumbers: number[] = [];
  const problems: SheetProblem[] = [];
  body.forEach((cellsOfRow, i) => {
    if (cellsOfRow.every((c) => cells.text(c) === undefined)) {
      return;
    }
    const rowNumber = i + 2;
    const get = (h: string): Cell => {
      const at = index.get(h);
      return at === undefined ? null : (cellsOfRow[at] ?? null);
    };
    rowNumbers.push(rowNumber);
    rows.push(
      parseRow(get, (column, message) => problems.push({ row: rowNumber, column, message })),
    );
  });
  if (rows.length > MAX_IMPORT_ROWS) {
    return {
      rows: [],
      rowNumbers: [],
      problems: [
        {
          row: null,
          column: null,
          message: `El archivo tiene ${rows.length} documentos y el tope por carga es ${MAX_IMPORT_ROWS}. Divídelo en varios archivos.`,
        },
      ],
    };
  }
  if (rows.length === 0) {
    problems.push({
      row: null,
      column: null,
      message: 'La hoja no tiene documentos para importar.',
    });
  }
  return { rows, rowNumbers, problems };
}
